import React from 'react';
import {
  Box,
  Chip,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography
} from '@mui/material';
import { useAutoTranslation } from 'src/hooks/useTranslate';
import { sameDiseaseName } from 'src/util/diseaseSearch';

const TAG_SX = { height: 18, fontSize: 10 };

/**
 * Which of a disease's codes to record, when the disease is in more than one
 * vocabulary (the pedigree's Diagnoses add and the re-code dialog).
 *
 * Each code is listed with its OWN name where it differs from the row's,
 * because that is the name recorded with it (TGCLOUD-1892): a Mondo group
 * holds "Fragile X syndrome" (OMIM, Orphanet) and "Fragile X chromosome"
 * (ICD-10-CM Q99.2). An ICD-10-CM category heading says "(category)". The
 * preferred code, first by Settings → Terminology, is marked, and nothing is
 * picked silently.
 *
 * @param {object} props
 * @param {object} props.option the disease option (util/diseaseSearch)
 * @param {string} props.selectedId the code chosen so far
 * @param {(id: string) => void} props.onChange
 * @param {string} [props.currentId] the code the record holds now, marked
 *   "Current" (the re-code dialog)
 * @param {(a: string, b: string) => boolean} [props.sameId]
 */
export default function DiseaseCodeChoice({
  option,
  selectedId,
  onChange,
  currentId = null,
  sameId = (a, b) => a === b
}) {
  const t = useAutoTranslation();
  const codes = option?.codingSystems || [];
  return (
    <Box sx={{ pt: 1 }}>
      <Typography variant="body2" sx={{ mb: 1.5 }}>
        <strong>{option?.name}</strong>{' '}
        {t(
          'exists in more than one coding system. Select the code to record for this person:'
        )}
      </Typography>
      <RadioGroup
        value={selectedId || ''}
        onChange={(e) => onChange(e.target.value)}
      >
        {codes.map((cs, idx) => (
          <FormControlLabel
            key={cs.id}
            value={cs.id}
            control={<Radio size="small" />}
            label={
              <Box sx={{ py: 0.25 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    flexWrap: 'wrap'
                  }}
                >
                  <span>{cs.label}</span>
                  <Typography
                    variant="caption"
                    sx={{ color: 'text.secondary', fontFamily: 'monospace' }}
                  >
                    {cs.id}
                  </Typography>
                  {cs.isLeaf === false && (
                    <Typography
                      variant="caption"
                      sx={{ color: 'text.secondary' }}
                    >
                      {t('(category)')}
                    </Typography>
                  )}
                  {idx === 0 && (
                    <Chip
                      label={t('Preferred')}
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={TAG_SX}
                    />
                  )}
                  {currentId && sameId(cs.id, currentId) && (
                    <Chip
                      label={t('Current')}
                      size="small"
                      variant="outlined"
                      sx={TAG_SX}
                    />
                  )}
                </Box>
                {cs.name && !sameDiseaseName(cs.name, option?.name) && (
                  <Typography
                    variant="caption"
                    data-testid={`code-own-name-${cs.id}`}
                    sx={{ color: 'text.secondary', display: 'block' }}
                  >
                    {t('Recorded as: {{name}}', { name: cs.name })}
                  </Typography>
                )}
              </Box>
            }
          />
        ))}
      </RadioGroup>
    </Box>
  );
}
